import { useState, useRef, useEffect, useCallback } from 'react';
import { Drawer, Button, Form, Input, Select, Upload, Space, Checkbox, Image, message, DatePicker, InputNumber, Switch, Cascader, Typography, Tag, Modal, Radio, Row, Col, Progress, Popconfirm, Alert, Pagination } from 'antd';
import './index.less';
import config from '@config';
import { useNavigate } from 'react-router-dom';
import { uploadFileApi, organizationsReviewedApi, organizationsRejectedApi } from '@/services/DC/Organization';
import dayjs from 'dayjs';
import { regionOptions } from '@/utils/region';
import { medTypeList, medOwnershipList, medTierList, medLevelList, medGradeList, orgTypeList } from '@/utils/constants';
import { organizationsSaveAndUpdateApi, organizationsInfoApi, organizationsValiNameApi, organizationsListApi } from '@/services/DC/Organization';
import { useDispatch } from 'react-redux';
import { organizationSliceActions } from '../../store/organizationSlice';
import { logOrganizationCreate, logOrganizationUpdate, logOrganizationApprove, logOrganizationReject } from '@/utils/organizationLogger';
import { ReviewStatusMap, prepareBCOSSUrl } from '@/utils/utils';
import OperationLogDrawer from '@/components/common/OperationLogDrawer';

const { Text, Link } = Typography;
const { setOrganizationFilter, setOrganizationCursor, setOrganizationMergeFilter, setOrganizationMergeCursor } = organizationSliceActions;

// 根据 creditCode 获取背景色的函数
// 算法：使用字符串哈希算法为不同登记号分配固定颜色，实现视觉分组效果
const getCreditCodeBackgroundColor = creditCode => {
    if (!creditCode) return '';

    const colors = ['#fff7e6', '#fff1f0', '#fff2e8', '#fffbe6', '#fcffe6', '#e6fffb', '#e6f7ff', '#f9f0ff'];

    // 哈希算法：hash = hash * 31 + charCode (使用位运算优化)
    let hash = 0;
    for (let i = 0; i < creditCode.length; i++) {
        const char = creditCode.charCodeAt(i);
        hash = (hash << 5) - hash + char; // 等价于 hash * 31 + char
        hash = hash & hash; // 转换为32位整数
    }

    // 取模映射到颜色索引，确保相同creditCode获得相同颜色
    const colorIndex = Math.abs(hash) % colors.length;
    return colors[colorIndex];
};

const datePickerTypes = [
    { label: '按日期选择', value: 'date' },
    { label: '按月份选择', value: 'month' },
    { label: '按年份选择', value: 'year' },
];

let isUploading = false; // 是否存在上传中的文件

// 新增机构
const OrganizationAdd = props => {
    const { openAddOrgDrawer, setOpenAddOrgDrawer, id, getOrganizationsList, setCurrentRecordId, setOpenViewOrgDrawer } = props;

    const navigate = useNavigate();

    const dispatch = useDispatch();

    const [form] = Form.useForm();
    const [addHospitalForm] = Form.useForm();

    const orgType = Form.useWatch('orgType', form);

    const [logoList, setLogoList] = useState([]);
    const [bizLicenseList, setBizLicenseList] = useState([]);
    const [fileList, setFileList] = useState([]);
    const [addHospitalModal, setAddHospitalModal] = useState(false);
    const [searchHospitalList, setSearchHospitalList] = useState(null); // 搜索院区的医院列表
    const [searchHospitalTotal, setSearchHospitalTotal] = useState(0); // 搜索医院总数
    const [searchHospitalPage, setSearchHospitalPage] = useState(1); // 搜索医院当前页
    const [searchHospitalPageSize, setSearchHospitalSize] = useState(10); // 搜索医院每页大小
    const [branchList, setBranchList] = useState([]); // 已选院区展示列表
    const [affiliatedList, setAffiliatedList] = useState([]); // 已选附属医院展示列表
    const [hasSelectedHospital, setHasSelectedHospital] = useState(false); // 是否有选中医院
    const [orgTypeChangeModal, setOrgTypeChangeModal] = useState(false); // 机构类型切换确认弹窗
    const [pendingOrgType, setPendingOrgType] = useState(null); // 待切换的机构类型
    const [reviewStatus, setReviewStatus] = useState(null); // 审核状态
    const [isSwitchStatus, setIsSwitchStatus] = useState(false); // 是否显示审核状态切换
    const [info, setInfo] = useState({}); // 机构信息
    const [openRecordDrawer, setOpenRecordDrawer] = useState(false); // 操作记录抽屉

    const selectedAreaRefs = useRef([]);
    const nameRepeatRefs = useRef(0);
    const [curMainBranchId, setCurMainBranchId] = useState(null); //设置当前主院区id
    const [loading, setLoading] = useState(false);
    const savedNameRefs = useRef(null); // 已保存的名称
    const prevOrgTypeRef = useRef(null); // 上一次的机构类型
    const duplicateNameRefs = useRef(null); // 重名的机构名称
    const beforeDataRefs = useRef(null); // 编辑前的表单数据快照

    const [fileShowProgress, setFileShowProgress] = useState(false);
    const [fileProgressPercent, setFileProgressPercent] = useState();

    const onClose = () => {
        setOpenAddOrgDrawer(false);
        clearFormRecord();
    };

    // 移除logo
    const delLogoList = () => {
        setLogoList([]);
        form.setFieldValue('logo', '');
    };

    // 处理机构类型切换
    const handleOrgTypeChange = newOrgType => {
        const currentOrgType = prevOrgTypeRef.current;

        // 如果是第一次选择或从"未分类"(0)切换到具体机构类型，直接切换
        if (currentOrgType === 0 || currentOrgType === null || currentOrgType === undefined) {
            prevOrgTypeRef.current = newOrgType;
            return;
        }

        // 如果是具体机构类型间切换或切换为"未分类"，需要确认
        if (currentOrgType !== newOrgType) {
            setPendingOrgType(newOrgType);
            setOrgTypeChangeModal(true);
        }
    };

    // 确认机构类型切换
    const confirmOrgTypeChange = () => {
        if (pendingOrgType !== null) {
            prevOrgTypeRef.current = pendingOrgType;
            setPendingOrgType(null);
        }
        setOrgTypeChangeModal(false);
    };

    // 取消机构类型切换
    const cancelOrgTypeChange = () => {
        setPendingOrgType(null);
        setOrgTypeChangeModal(false);
        // 恢复到之前的值
        form.setFieldValue('orgType', prevOrgTypeRef.current);
    };

    // 清除记录
    const clearFormRecord = () => {
        form.resetFields();
        setLogoList([]);
        setBizLicenseList([]);
        setFileList([]);
        selectedAreaRefs.current = [];
        setCurMainBranchId(null);
        setBranchList([]);
        setAffiliatedList([]);
        prevOrgTypeRef.current = null;
        setPendingOrgType(null);
        setOrgTypeChangeModal(false);
        nameRepeatRefs.current = 0; // 重置重名校验状态
        savedNameRefs.current = null; // 重置已保存的名称
        duplicateNameRefs.current = null; // 重置重名机构名称
        beforeDataRefs.current = null; // 重置编辑前数据快照
        setReviewStatus(null); // 重置审核状态
        setIsSwitchStatus(false); // 重置审核状态切换
        setInfo({}); // 重置机构信息
        setOpenRecordDrawer(false); // 重置操作记录抽屉
    };

    // 保存继续新增
    const onSaveOrAdd = () => {
        onSave(1);
    };

    // 日期格式处理
    const formatDateByPickerTypeForSave = (date, pickerType) => {
        let result = '';
        if (date && dayjs(date).isValid()) {
            switch (pickerType) {
                case 'date':
                    result = dayjs(date).format('YYYY-MM-DD');
                    break;
                case 'month':
                    result = dayjs(date).format('YYYY-MM-01');
                    break;
                case 'year':
                    result = dayjs(date).format('YYYY-01-01');
                    break;
                default:
                    result = dayjs(date).format('YYYY-MM-DD');
                    break;
            }
        }

        return result;
    };

    // 保存并审核通过
    // 待审核 和 已驳回 时，可保存并审核通过
    const onSaveAndApprove = async () => {
        try {
            // 调用 onSave 并传入 handleApprove 作为成功回调
            onSave(1, handleApprove);
        } catch (error) {
            console.error('保存并审核通过失败', error);
            message.error('保存并审核通过失败：' + error.message);
        }
    };

    // 保存机构信息
    // 新增时，onSuccess 为 null，编辑时，onSuccess 为 handleApprove，用于实现保存后自动审核通过
    const onSave = (type, onSuccess) => {
        // 存在文件上传中时 - 禁止提交
        if (isUploading) {
            return message.error('存在文件上传中，请等待上传完成后再提交');
        }
        if (nameRepeatRefs.current === 1 || nameRepeatRefs.current === 3) {
            form.setFields([
                {
                    errors: [
                        <>
                            已有相同名称的机构，<Link onClick={() => handleViewDuplicateOrg(nameRepeatRefs.current)}>立即查看</Link>
                        </>,
                    ],
                    name: 'name',
                },
            ]);
            nameRepeatRefs.current = 0;
            return;
        }
        if (nameRepeatRefs.current === 2) {
            form.setFields([{ errors: ['机构名称无效，请准确填写机构名称'], name: 'name' }]);
            nameRepeatRefs.current = 0;
            return;
        }
        form.validateFields()
            .then(async values => {
                const newValues = { ...values, id };
                newValues.aliases = values.aliases ? JSON.stringify(values.aliases) : undefined;
                newValues.establishedAt = values.establishedAt ? formatDateByPickerTypeForSave(values.establishedAt, values.establishedDateType) : undefined;
                newValues.mainBranchId = id ? curMainBranchId || id : 0; // 新增时传参 - 0； 编辑时 - 主院区id || 当前编辑机构id；
                newValues.mainBranchCampusId = curMainBranchId || 0; // 设置当前主院区id
                newValues.mainBranchCampus = 0;

                // 没有院区时，传参[-1]
                if (id && (!values.branchIds || values.branchIds.length === 0)) {
                    newValues.branchIds = [-1];
                    newValues.mainBranchId = 0; // 编辑时 - 若院区被清空 则主院区id为0
                    // 院区仅有一个 且 是当前院区时
                } else if (id && values.branchIds && values.branchIds.length === 1 && values.branchIds[0] === id) {
                    newValues.branchIds = [-1];
                    // 院区存在多个 且 当前院区不在添加院区之内时，则将当前院区id一并传参
                } else if (id && values.branchIds && values.branchIds.length > 1 && !values.branchIds.includes(id)) {
                    newValues.branchIds = [...values.branchIds, id];
                }

                // 所在地区 - 拆分保存
                if (values.areas && values.areas.length > 0) {
                    newValues.province = selectedAreaRefs.current[0];
                    newValues.city = selectedAreaRefs.current[1];
                    newValues.district = selectedAreaRefs.current[2];
                    newValues.provinceCode = values.areas[0];
                    newValues.cityCode = values.areas[1];
                    newValues.districtCode = values.areas[2];
                }
                delete newValues.areas;

                // 医疗机构类型、医疗机构性质、医疗机构层级留空时转为 ''，因为 null 传给 API 会不更新
                if (newValues.medType === null || newValues.medType === undefined) {
                    newValues.medType = '';
                }
                if (newValues.medOwnership === null || newValues.medOwnership === undefined) {
                    newValues.medOwnership = '';
                }
                if (newValues.medTier === null || newValues.medTier === undefined) {
                    newValues.medTier = '';
                }

                // 成立时间为空时，清除成立类型
                if (!values.establishedAt) {
                    newValues.establishedDateType = '';
                }

                setLoading(true);
                try {
                    const res = await organizationsSaveAndUpdateApi(newValues);
                    setLoading(false);
                    if (res && res.status_code === 200) {
                        message.success('保存成功');
                        getOrganizationsList();

                        if (type !== 1 || id) {
                            // 保存继续新增时，不关闭弹窗
                            setOpenAddOrgDrawer(false);
                        }

                        // 上报操作日志
                        try {
                            if (id) {
                                // 编辑机构 - 使用表单数据
                                const beforeInfo = beforeDataRefs.current || {};
                                const afterInfo = form.getFieldsValue();
                                await logOrganizationUpdate(id, beforeInfo, afterInfo);
                            } else {
                                // 新增机构 - 使用表单数据
                                const orgId = res.data?.id || 0;
                                const afterInfo = form.getFieldsValue();
                                await logOrganizationCreate(orgId, afterInfo);
                            }
                        } catch (logError) {
                            console.error('操作日志上报失败', logError);
                            // 日志上报失败不影响主流程
                        }

                        // 上报完日志，再清理表单数据
                        clearFormRecord();

                        // 如果提供了成功回调，则执行它
                        if (onSuccess && typeof onSuccess === 'function') {
                            // 延迟执行回调，确保状态更新完成
                            setTimeout(() => {
                                onSuccess(type);
                            }, 100);
                        }
                    } else {
                        setLoading(false);
                        message.error(res.message);

                        // 上报失败日志
                        try {
                            if (id) {
                                const beforeInfo = beforeDataRefs.current || {};
                                const afterInfo = form.getFieldsValue();
                                await logOrganizationUpdate(id, beforeInfo, afterInfo, false, res.message);
                            } else {
                                // 新增失败时，entityId 使用 0
                                const afterInfo = form.getFieldsValue();
                                await logOrganizationCreate(0, afterInfo, false, res.message);
                            }
                        } catch (logError) {
                            console.error('操作日志上报失败', logError);
                        }
                    }
                } catch (error) {
                    setLoading(false);
                    throw new Error(error);
                }
            })
            .catch(() => {
                const timer = setTimeout(() => {
                    const errorList = document.querySelectorAll('.ant-form-item-has-error');
                    errorList[0]?.scrollIntoView({
                        block: 'center',
                        behavior: 'smooth',
                    });
                    clearTimeout(timer);
                }, 100);
            });
    };

    // 根据id查询机构信息
    const getOrganizationsInfo = useCallback(async () => {
        const res = await organizationsInfoApi({ id });
        if (res && res.status_code === 200 && res.data) {
            const values = res.data;
            savedNameRefs.current = values.name;
            const newValues = { ...values };
            newValues.aliases = values.aliases ? JSON.parse(values.aliases) : undefined;
            newValues.establishedAt = values.establishedAt ? dayjs(values.establishedAt) : undefined;
            newValues.establishedDateType = values.establishedDateType ? values.establishedDateType : undefined;
            newValues.medLevel = values.medLevel ? values.medLevel : undefined;
            newValues.medGrade = values.medGrade ? values.medGrade : undefined;

            // 所在地区 - 合并回显
            const code = [];
            const codeName = [];
            if (values.provinceCode) {
                code[0] = values.provinceCode;
                codeName[0] = values.province;
            }
            if (values.cityCode) {
                code[1] = values.cityCode;
                codeName[1] = values.city;
            }
            if (values.districtCode) {
                code[2] = values.districtCode;
                codeName[2] = values.district;
            }
            newValues.areas = code;
            selectedAreaRefs.current = codeName;

            // logo回显
            if (values.logo) {
                setLogoList([getFileUrl(values.logo)]);
            }

            // 营业执照回显
            if (values.bizLicense) {
                setBizLicenseList([getFileUrl(values.bizLicense)]);
            }

            // 附件回显
            if (values.files) {
                setFileList(JSON.parse(values.files));
            }

            // 院区回显
            if (values.branchList) {
                // 院区排序和分组：
                // 有"登记号（creditCode）"的院区排在前
                // 相同登记号的院区归为一组
                const sortedBranchList = [...values.branchList].sort((a, b) => {
                    // 有登记号的排在前面
                    const aHasCreditCode = !!a.creditCode;
                    const bHasCreditCode = !!b.creditCode;

                    if (aHasCreditCode && !bHasCreditCode) {
                        return -1;
                    }
                    if (!aHasCreditCode && bHasCreditCode) {
                        return 1;
                    }

                    // 如果都有登记号，按登记号分组（相同登记号的排在一起）
                    if (aHasCreditCode && bHasCreditCode) {
                        const creditCodeCompare = (a.creditCode || '').localeCompare(b.creditCode || '');
                        if (creditCodeCompare !== 0) {
                            return creditCodeCompare;
                        }
                        // 相同登记号时，主院区排在前
                        if (a.mainBranchCampus === 1 && b.mainBranchCampus !== 1) {
                            return -1;
                        }
                        if (a.mainBranchCampus !== 1 && b.mainBranchCampus === 1) {
                            return 1;
                        }
                        // 最后按ID排序
                        return a.id - b.id;
                    }

                    // 都没有登记号时，主院区排在前
                    if (a.mainBranchCampus === 1 && b.mainBranchCampus !== 1) {
                        return -1;
                    }
                    if (a.mainBranchCampus !== 1 && b.mainBranchCampus === 1) {
                        return 1;
                    }

                    // 最后按ID排序
                    return a.id - b.id;
                });

                setBranchList(sortedBranchList);

                newValues.branchIds = sortedBranchList.map(item => item.id);
                const mainId = sortedBranchList.find(item => item.mainBranchCampus === 1)?.id || null; // mainBranchCampus === 1 主院区标识
                setCurMainBranchId(mainId); // 设置当前主院区id
            }

            // 附属医院回显
            if (values.affiliatedList) {
                setAffiliatedList(values.affiliatedList);
                newValues.affiliatedIds = values.affiliatedList.map(item => item.id);
            }

            // 医疗机构类型、医疗机构性质、医疗机构层级，db DEFAULT ''
            // 前端展示时，如果是 ''，则转为 undefined，以适配 Select 组件的空值处理
            newValues.medType = newValues.medType === '' ? undefined : newValues.medType;
            newValues.medOwnership = newValues.medOwnership === '' ? undefined : newValues.medOwnership;
            newValues.medTier = newValues.medTier === '' ? undefined : newValues.medTier;

            // 床位数、员工数、年门急诊量为0时转为 undefined
            newValues.bedCount = newValues.bedCount === 0 ? undefined : newValues.bedCount;
            newValues.employeeCount = newValues.employeeCount === 0 ? undefined : newValues.employeeCount;
            newValues.annualOutpatient = newValues.annualOutpatient === 0 ? undefined : newValues.annualOutpatient;

            newValues.country = newValues.country ? newValues.country : undefined;

            form.setFieldsValue(newValues);

            // 收集编辑前的表单数据快照（用于操作日志）
            setTimeout(() => {
                beforeDataRefs.current = form.getFieldsValue();
            }, 0);

            // 设置初始机构类型
            prevOrgTypeRef.current = values.orgType;

            // 设置审核状态
            setReviewStatus(values.reviewStatus);

            // 设置机构信息
            setInfo(values);
        }
    }, [id, form]);

    // 添加医院
    const addHospital = () => {
        setAddHospitalModal(true);
        setHasSelectedHospital(false);
        setSearchHospitalList(null);
    };

    // 查看医院
    const viewHospital = curId => {
        setCurrentRecordId(curId);
        setOpenViewOrgDrawer(true);
        setOpenAddOrgDrawer(false);
    };

    // 编辑医院
    const editHospital = curId => {
        setCurrentRecordId(curId);
    };

    // 审核通过
    const handleApprove = async btnType => {
        try {
            const res = await organizationsReviewedApi({ id });
            if (res && res.status_code === 200) {
                message.success('审核通过成功');
                setIsSwitchStatus(false);
                // 如果不是点击 ‘保存并审核通过’，则获取机构信息
                if (btnType !== 1) {
                    getOrganizationsInfo();
                }
                getOrganizationsList();

                // 上报操作日志
                try {
                    await logOrganizationApprove(id);
                } catch (logError) {
                    console.error('操作日志上报失败', logError);
                    // 日志上报失败不影响主流程
                }
            }
        } catch (error) {
            console.error('审核通过失败', error);
            message.error('审核通过失败');
        }
    };

    // 审核驳回
    const handleReject = async () => {
        try {
            const res = await organizationsRejectedApi({ id });
            if (res && res.status_code === 200) {
                message.success('审核驳回成功');
                setIsSwitchStatus(false);
                getOrganizationsInfo();
                getOrganizationsList();

                // 上报操作日志
                try {
                    await logOrganizationReject(id, '信息无效');
                } catch (logError) {
                    console.error('操作日志上报失败', logError);
                    // 日志上报失败不影响主流程
                }
            }
        } catch (error) {
            console.error('审核驳回失败', error);
            message.error('审核驳回失败');
        }
    };

    // 提交审核（保留原有函数，但不再使用）
    const sumitReview = async () => {
        if (reviewStatus) {
            const reqApi = reviewStatus === 1 ? organizationsReviewedApi : organizationsRejectedApi;
            try {
                const res = await reqApi({ id });
                if (res && res.status_code === 200) {
                    message.success('提交审核成功');
                    setIsSwitchStatus(false);
                    getOrganizationsInfo();
                    getOrganizationsList();

                    // 上报操作日志
                    try {
                        if (reviewStatus === 1) {
                            // 审核通过
                            await logOrganizationApprove(id);
                        } else {
                            // 审核驳回 - 这里使用默认的驳回理由，实际应用中可能需要用户输入
                            await logOrganizationReject(id, '信息无效');
                        }
                    } catch (logError) {
                        console.error('操作日志上报失败', logError);
                        // 日志上报失败不影响主流程
                    }
                }
            } catch (error) {
                console.error('审核提交失败', error);
                message.error('提交审核失败');
            }
        } else {
            message.warning('请选择审核状态');
        }
    };

    const onChange = e => {
        setReviewStatus(e.target.value);
    };

    // 打开操作记录
    const openRecord = () => {
        setOpenRecordDrawer(true);
    };

    // 准备logo url，兼容用户中心上传的 upload 打头的 logo url
    const getFileUrl = logo => {
        let url = '';

        if (!!logo && typeof logo === 'string') {
            if (logo.startsWith('upload/')) {
                url = `${config.FILE_PREFIX}/${logo}`;
            } else {
                url = prepareBCOSSUrl(logo);
            }
        }

        return url;
    };

    // 移除附件
    const deleteFile = idx => {
        const list = fileList.filter((_, index) => index !== idx);
        form.setFieldValue('files', JSON.stringify(list));
        setFileList(list);
    };

    // 上传文件
    const uploadFile = async (value, type) => {
        console.log(value);
        const formData = new FormData();
        formData.append('file', value.file);
        formData.append('type', 101);

        setFileShowProgress(true);
        isUploading = true;
        const configParams = {
            onUploadProgress: progress => {
                const percents = Math.round((progress.loaded * 100) / progress.total);
                setFileProgressPercent(percents); // 更新进度条
            },
        };
        const res = await uploadFileApi(formData, configParams);
        if (res && res.status_code === 200) {
            const list = [getFileUrl(res.data)];
            // logo
            if (type === 1) {
                form.setFieldValue('logo', res.data);
                setLogoList(list);
            }
            // 营业执照
            if (type === 2) {
                form.setFieldValue('bizLicense', res.data);
                setBizLicenseList(list);
            }
            // 附件
            if (type === 3) {
                const item = { name: value.file.name, url: getFileUrl(res.data), type: value.file.type };
                const newList = [...fileList, item];
                form.setFieldValue('files', JSON.stringify(newList));
                setFileList(newList);
            }
            setFileShowProgress(false);
            setFileProgressPercent(undefined);
            isUploading = false;
        }
    };

    // 上传图片格式校验
    const beforeUpload = file => {
        const isJpgOrPng = file.type === 'image/jpeg' || file.type === 'image/png' || file.type === 'image/jpg';
        if (!isJpgOrPng) {
            message.error('只能上传 jpg/png/jpeg 格式');
            return false;
        }
        const isLt5M = file.size / 1024 / 1024 < 5;
        if (!isLt5M) {
            message.error('图片大小不超过5MB');
            return false;
        }
        return isJpgOrPng && isLt5M;
    };

    // 上传附件校验
    const filesBeforeUpload = file => {
        const reg = /.+\.(jpe?g|png|docx?|xlsx?|pptx?|pdf|psd|ai|zip|rar)$/;
        if (!reg.test(file.name)) {
            message.error('仅支持图片（JPG/PNG）、Office 文档（Word/Excel/PPT）、PDF、PSD、Ai及压缩包');
            return false;
        }
        const isLt500M = file.size / 1024 / 1024 < 500;
        if (!isLt500M) {
            message.error('单个文件不超过500MB');
            return false;
        }
        return isLt500M;
    };

    // 搜索院区/附属医院 - 保存
    const handleOk = async () => {
        addHospitalForm.validateFields().then(values => {
            if (values.orgs && values.orgs.length > 0) {
                const checkList = searchHospitalList.filter((_, index) => values.orgs[index]);
                setSearchHospitalList([]);

                const type = form.getFieldValue('orgType');
                // 医疗机构 - 院区
                if (type === 1) {
                    let resList = [...branchList, ...checkList];

                    // 编辑模式且当前医院不在院区列表中时，添加当前医院并设为主院区
                    if (id && !resList.some(item => item.id === id)) {
                        // 需要获取当前医院信息来添加到院区列表
                        const currentHospital = {
                            id: id,
                            name: form.getFieldValue('name') || '当前机构',
                            address: form.getFieldValue('address') || '',
                        };
                        resList = [currentHospital, ...resList];
                        // 设置当前医院为主院区
                        setCurMainBranchId(id);
                    }

                    setBranchList(resList);
                    form.setFieldValue(
                        'branchIds',
                        resList.map(item => item.id),
                    );
                }
                // 科研院校 - 附属医院
                if (type === 4) {
                    const resList = [...affiliatedList, ...checkList];
                    setAffiliatedList(resList);
                    form.setFieldValue(
                        'affiliatedIds',
                        resList.map(item => item.id),
                    );
                }

                setAddHospitalModal(false);
                addHospitalForm.resetFields();
                setHasSelectedHospital(false);
            }
        });
    };

    // 移除医院/附属医院
    const removeHospital = delId => {
        Modal.confirm({
            title: '提示',
            content: '确定移除该院区？',
            okText: '确定',
            cancelText: '取消',
            onOk: () => {
                const type = form.getFieldValue('orgType');
                // 如果移除了主院区，则记录当前主院区null
                if (curMainBranchId === delId) {
                    setCurMainBranchId(null);
                }
                // 医疗机构 - 院区
                if (type === 1) {
                    let resList = branchList.filter(item => item.id !== delId);

                    // 如果移除后只剩下当前医院，则也移除当前医院（清空院区列表）
                    if (id && resList.length === 1 && resList[0].id === id) {
                        resList = [];
                        setCurMainBranchId(null);
                    }

                    setBranchList(resList);
                    form.setFieldValue(
                        'branchIds',
                        resList.map(item => item.id),
                    );
                }
                // 科研院校 - 附属医院
                if (type === 4) {
                    const resList = affiliatedList.filter(item => item.id !== delId);
                    setAffiliatedList(resList);
                    form.setFieldValue(
                        'affiliatedIds',
                        resList.map(item => item.id),
                    );
                }
            },
        });
    };

    // 搜索院区/附属医院 - 取消
    const handleCancel = () => {
        setAddHospitalModal(false);
        addHospitalForm.resetFields();
        setSearchHospitalList(null);
        setSearchHospitalTotal(0);
        setSearchHospitalPage(1);
        setSearchHospitalSize(10);
        setHasSelectedHospital(false);
    };

    // 院区搜索
    const onSearchHospitalSubmit = async (values, page = 1, size = 10) => {
        const params = { keyword: values.keyword, page, size };
        // 所在地区 - 拆分保存
        if (values.areas && values.areas.length > 0) {
            params.provinceCode = values.areas[0];
            params.cityCode = values.areas[1];
            params.districtCode = values.areas[2];
        }
        // 医疗机构 - 院区 - 参数
        if (orgType === 1) {
            params.mainBranchId = 0;
            // 限定只检索医疗机构类型
            params.orgType = [1];
        }
        // 科研院校 - 附属医院 - 参数
        if (orgType === 4) {
            params.affiliatedToId = 0;
            // 限定只检索医疗机构类型
            params.orgType = [1];
        }
        const res = await organizationsListApi(params);
        if (res && res.status_code === 200 && res.data) {
            setSearchHospitalList(res.data.records || []);
            setSearchHospitalTotal(res.data.total || 0);
            setSearchHospitalPage(page);
            setSearchHospitalSize(size);
            // 检查是否有已选中的医院
            const hasSelected = (res.data.records || []).some(item => isHospitalSelected(item.id));
            setHasSelectedHospital(hasSelected);
        }
    };

    // 分页处理
    const handleSearchHospitalPageChange = (page, size) => {
        const formValues = addHospitalForm.getFieldsValue();
        onSearchHospitalSubmit(formValues, page, size);
    };

    // 所在地区选择
    const handleAreasChange = (_, selectedOptions) => {
        selectedAreaRefs.current = selectedOptions.map(item => item.label);
    };

    // 立即查看重名机构
    const handleViewDuplicateOrg = type => {
        if (duplicateNameRefs.current) {
            Modal.confirm({
                title: '提示',
                content: '跳转页面当前机构改动数据将丢失，确定查看？',
                okText: '确定',
                centered: true,
                onOk: () => {
                    // 设置机构列表的搜索条件
                    if (type === 1) {
                        dispatch(
                            setOrganizationFilter({
                                keywordList: [duplicateNameRefs.current],
                                page: 1,
                                size: 20,
                            }),
                        );
                        dispatch(setOrganizationCursor(0));
                    }
                    // 重名机构且为被合并机构 - 跳转合并列表
                    if (type === 3) {
                        dispatch(
                            setOrganizationMergeFilter({
                                keywordList: [duplicateNameRefs.current],
                                page: 1,
                                size: 20,
                            }),
                        );
                        dispatch(setOrganizationMergeCursor(0));
                        navigate('/dc/organization/merge-management');
                    }
                    // 关闭当前抽屉
                    setOpenAddOrgDrawer(false);
                    clearFormRecord();
                },
            });
        }
    };

    /**
     * 验证重名规则如下
     * - 名称存在重复 - 1
     * - 机构名称无效 - 2
     * - 名称存在重复且为重名机构为被合并机构 - 3
     */
    const handleNameChange = async e => {
        const value = e.target.value;
        // 如果当前编辑名称 依然是当前院区名称，则跳过校验
        if (!value || value === savedNameRefs.current) {
            return;
        }
        const res = await organizationsValiNameApi({ name: value });
        if (res && res.status_code === 200) {
            nameRepeatRefs.current = res.data;
            duplicateNameRefs.current = value; // 保存重名的机构名称
            if (res.data === 1 || res.data === 3) {
                form.setFields([
                    {
                        errors: [
                            <>
                                已有相同名称的机构，<Link onClick={() => handleViewDuplicateOrg(res.data)}>立即查看</Link>
                            </>,
                        ],
                        name: 'name',
                    },
                ]);
            }
            if (res.data === 2) {
                form.setFields([{ errors: ['机构名称无效，请准确填写机构名称'], name: 'name' }]);
            }
        }
    };

    // blur事件处理函数
    const handleNameBlur = async e => {
        const value = e.target.value?.trim();
        if (!value) {
            // 清空输入时，重置重名校验状态
            nameRepeatRefs.current = 0;
            return;
        }
        await handleNameChange(e);
    };

    // 搜索医院是否可选中
    const disabledHospital = rowId => {
        // 当前机构不能选择
        if (rowId === id) {
            return true;
        }
        const type = form.getFieldValue('orgType');
        // 医疗机构 - 院区
        if (type === 1) {
            const isExist = branchList.some(item => item.id === rowId);
            return isExist;
        }
        // 科研院校 - 附属医院
        if (type === 4) {
            const isExist = affiliatedList.some(item => item.id === rowId);
            return isExist;
        }
    };

    // 检查是否已经是院区/附属医院
    const isHospitalSelected = rowId => {
        const type = form.getFieldValue('orgType');
        // 医疗机构 - 院区
        if (type === 1) {
            return branchList.some(item => item.id === rowId);
        }
        // 科研院校 - 附属医院
        if (type === 4) {
            return affiliatedList.some(item => item.id === rowId);
        }
        return false;
    };

    // 处理复选框状态变化
    const handleCheckboxChange = () => {
        // 延迟一个事件循环来获取更新后的表单值
        setTimeout(() => {
            const values = addHospitalForm.getFieldsValue();
            const hasSelected = values.orgs && values.orgs.some(checked => checked);
            setHasSelectedHospital(hasSelected);
        }, 0);
    };

    useEffect(() => {
        if (openAddOrgDrawer && id) {
            getOrganizationsInfo();
        }
    }, [openAddOrgDrawer, id, getOrganizationsInfo]);

    return (
        <>
            <Drawer
                closable={{ placement: 'end' }}
                className="add-organization-drawer"
                title={
                    id ? (
                        <span>
                            编辑机构 <Text type="secondary">#{id}</Text>
                        </span>
                    ) : (
                        '新增机构'
                    )
                }
                placement="bottom"
                open={openAddOrgDrawer}
                size="100%"
                onClose={onClose}
                mask={{ closable: false }}
                footer={
                    <div className="org-footer">
                        <Space>
                            {id && info?.reviewStatus !== undefined && (
                                <span>
                                    <span>审核状态：</span>
                                    <span>
                                        <Text type={ReviewStatusMap[info.reviewStatus]?.type}>{ReviewStatusMap[info.reviewStatus]?.text}</Text>
                                    </span>
                                </span>
                            )}

                            {id && (info?.reviewStatus === 0 || isSwitchStatus) && (
                                <div className="review-status">
                                    <Space>
                                        <Button type="primary" size="small" onClick={() => handleApprove()}>
                                            审核通过
                                        </Button>
                                        <Popconfirm title="确定驳回该机构信息吗？" onConfirm={() => handleReject()} okText="确定" cancelText="取消">
                                            <Button danger size="small">
                                                审核驳回
                                            </Button>
                                        </Popconfirm>
                                        {isSwitchStatus && (
                                            <Button type="link" size="small" onClick={() => setIsSwitchStatus(false)} placement="bottom">
                                                取消
                                            </Button>
                                        )}
                                    </Space>
                                </div>
                            )}
                            {id && !!info?.reviewStatus && !isSwitchStatus && (
                                <Button type="link" size="small" onClick={() => setIsSwitchStatus(!isSwitchStatus)}>
                                    重新审核
                                </Button>
                            )}
                        </Space>
                        <Space>
                            <Button onClick={onClose}>取消</Button>
                            {id && (info?.reviewStatus === 0 || info?.reviewStatus === 2) && (
                                <Button onClick={() => onSaveAndApprove()} loading={loading}>
                                    保存并审核通过
                                </Button>
                            )}
                            {!id && (
                                <Button onClick={onSaveOrAdd} loading={loading}>
                                    保存并继续新增
                                </Button>
                            )}
                            <Button type="primary" onClick={onSave} loading={loading}>
                                保存
                            </Button>
                        </Space>
                    </div>
                }
            >
                <Row justify="center">
                    <Col md={24} lg={20} xl={18}>
                        {id && (
                            <Button
                                type="link"
                                onClick={openRecord}
                                icon={
                                    <span className="anticon">
                                        <i className="bi bi-arrow-bar-left"></i>
                                    </span>
                                }
                                className="org-record-btn"
                                style={{ marginBottom: 16 }}
                            >
                                操作记录
                            </Button>
                        )}
                        <Form form={form} labelCol={{ sm: { span: 24 }, md: { span: 6 }, lg: { span: 4 } }} wrapperCol={{ sm: { span: 24 }, md: { span: 18 }, lg: { span: 20 } }} initialValues={{ country: 'cn' }}>
                            <div className="module-title">基本信息</div>
                            <Form.Item label="机构类型" name="orgType" rules={[{ required: true, message: '请选择机构类型' }]}>
                                <Select placeholder="选择机构类型" allowClear onChange={handleOrgTypeChange}>
                                    {orgTypeList
                                        && !!orgTypeList.length
                                        && orgTypeList.map((item, index) => (
                                            <Select.Option key={index} value={item.value}>
                                                {item.label}
                                            </Select.Option>
                                        ))}
                                </Select>
                            </Form.Item>

                            <Form.Item label="机构名称" name="name" rules={[{ required: true, message: '请输入机构名称' }]}>
                                <Input placeholder="输入机构名称" maxLength={100} showCount onBlur={handleNameBlur} />
                            </Form.Item>

                            <Form.Item name="aliases" label="别称" extra="最多添加10个别称，可标为曾用名，别称和曾用名可用于用户端快速检索准确地检索到单位名称，别称可展示在用户端提高机构辨识度">
                                <Form.List name="aliases">
                                    {(fields, { add, remove, move }) => (
                                        <>
                                            {fields.length < 10 && (
                                                <div onClick={() => add()} className="add-aliases">
                                                    <i className="bi bi-plus-circle"></i>
                                                    <span>添加别称</span>
                                                </div>
                                            )}

                                            {fields.map((field, index) => (
                                                <div key={field.key} className="extra-name-wrap">
                                                    <Form.Item className="extra-name-input" {...field} name={[field.name, 'name']}>
                                                        <Input placeholder="输入别称" />
                                                    </Form.Item>
                                                    <div className="extra-name-right">
                                                        <Form.Item {...field} name={[field.name, 'former']} valuePropName="checked">
                                                            <Checkbox>曾用名</Checkbox>
                                                        </Form.Item>
                                                        <Space>
                                                            {index !== 0 && (
                                                                <span className="fieldOperation moveUp" onClick={() => move(index, index - 1)}>
                                                                    <i className="bi bi-arrow-up-circle"></i>
                                                                </span>
                                                            )}
                                                            {index !== fields.length - 1 && (
                                                                <span className="fieldOperation moveDown" onClick={() => move(index, index + 1)}>
                                                                    <i className="bi bi-arrow-down-circle"></i>
                                                                </span>
                                                            )}
                                                            <span className="fieldOperation remove" onClick={() => remove(field.name)}>
                                                                <i className="bi bi-x-circle"></i>
                                                            </span>
                                                        </Space>
                                                    </div>
                                                </div>
                                            ))}
                                        </>
                                    )}
                                </Form.List>
                            </Form.Item>

                            <Form.Item label="简称" name="shortName" extra="简称不对外展示，主要用于用户端提升检索机构的准确性">
                                <Input placeholder="输入简称" maxLength={100} showCount />
                            </Form.Item>

                            <Form.Item label="显示名称" name="displayName" extra="一般情况，平台用户前端默认显示上方的“机构名称”；若对显示名称有特别要求，可填写“显示名称”，一经填写，全平台仅展示“显示名称”。">
                                <Input placeholder="输入显示名称" maxLength={100} showCount />
                            </Form.Item>

                            <Form.Item name="logo" label="Logo" extra="图片尺寸不小于 300*300px，支持 JPG/PNG格式" className="logo-image">
                                {logoList.length > 0 && (
                                    <div className="logo-image-wrap">
                                        <Image width={300} src={logoList[0]} style={{ marginBottom: 10 }} alt="机构 Logo 预览" />
                                        <i className="bi bi-x-circle"></i>
                                    </div>
                                )}

                                <div>
                                    <Upload beforeUpload={beforeUpload} fileList={logoList} customRequest={e => uploadFile(e, 1)} maxCount={1}>
                                        <Button icon={<i className="bi bi-upload"></i>}>{logoList.length > 0 ? '更改图片' : '上传图片'}</Button>
                                    </Upload>
                                </div>
                            </Form.Item>

                            <Form.Item label="简介">
                                <Form.Item name="introduction">
                                    <Input.TextArea placeholder="输入简介，尽量从官网简介页面等可信来源获取" rows={5} />
                                </Form.Item>
                                <Form.Item extra="输入机构简介来源网址，建议填写机构官网“简介”页面的链接（而不是机构官网“首页”链接），便于后续技术自动抓取信息）">
                                    <Space.Compact style={{ width: '100%' }}>
                                        <Input readOnly style={{ width: 88, textAlign: 'center' }} tabIndex={-1} value="简介来源" />
                                        <Form.Item name="introductionSrc" noStyle rules={[{ pattern: /^https?:\/\/(.+\.)?.+\..+/i, message: '请输入正确的网址，如 https://www.pumch.cn/research/gudie.html' }]}>
                                            <Input placeholder="输入机构简介来源网址，如 https://www.pumch.cn/research/gudie.html" />
                                        </Form.Item>
                                    </Space.Compact>
                                </Form.Item>
                            </Form.Item>

                            {/* 政府机构无成立时间字段 */}
                            {orgType !== 2 && (
                                <>
                                    <Form.Item extra="可按日期、按月份或按年份填写成立时间，请参考医院官方公布信息填写" label="成立时间">
                                        <Space>
                                            <Form.Item name="establishedDateType" noStyle initialValue="date">
                                                <Select options={datePickerTypes} style={{ width: '140px' }} placeholder="选择时间类型" />
                                            </Form.Item>
                                            <Form.Item noStyle shouldUpdate>
                                                {({ getFieldValue }) => {
                                                    return (
                                                        <Form.Item name="establishedAt" noStyle>
                                                            <DatePicker picker={getFieldValue('establishedDateType') || 'date'} />
                                                        </Form.Item>
                                                    );
                                                }}
                                            </Form.Item>
                                        </Space>
                                    </Form.Item>
                                </>
                            )}

                            {/* 科研院校有以下字段：附属医院 */}
                            {orgType === 4 && (
                                <>
                                    <Form.Item label="附属医院" name="affiliatedIds">
                                        <div onClick={addHospital} className="add-aliases">
                                            <i className="bi bi-plus-circle"></i>
                                            <span>添加附属医院</span>
                                        </div>
                                        {affiliatedList
                                            && !!affiliatedList.length
                                            && affiliatedList.map(item => (
                                                <div className="hospital-list-item" key={item.id}>
                                                    <div className="hospital-list-item-title">
                                                        <span>
                                                            {item.name}
                                                            <Text type="secondary"> #{item.id}</Text>
                                                        </span>
                                                        <span>{item.address}</span>
                                                    </div>
                                                    <Button type="link" onClick={() => removeHospital(item.id)}>
                                                        移除
                                                    </Button>
                                                </div>
                                            ))}
                                    </Form.Item>
                                </>
                            )}

                            {/* 医疗机构类型有以下字段：医疗机构类型、医疗机构性质、医疗机构层级、等级、国家医学中心、国家区域医疗中心、国家级临床重点专科、省级临床重点专科、市级临床重点专科 */}
                            {orgType === 1 && (
                                <>
                                    <Form.Item label="登记号" name="creditCode" extra="准确填写医疗机构执业许可证登记号" rules={[{ pattern: /^[a-z\d-]+$/i, message: '登记号格式不正确，仅支持字母和数字' }]} maxLength={50}>
                                        <Input placeholder="输入医疗机构执业许可证登记号" maxLength={50} />
                                    </Form.Item>
                                    <Form.Item label="医疗机构类型" name="medType">
                                        <Select placeholder="选择医疗机构类型" allowClear>
                                            {medTypeList.map((item, index) => (
                                                <Select.Option value={item} key={index}>
                                                    {item}
                                                </Select.Option>
                                            ))}
                                        </Select>
                                    </Form.Item>

                                    <Form.Item label="医疗机构性质" name="medOwnership">
                                        <Select placeholder="选择医疗机构性质" allowClear>
                                            {medOwnershipList.map((item, index) => (
                                                <Select.Option value={item} key={index}>
                                                    {item}
                                                </Select.Option>
                                            ))}
                                        </Select>
                                    </Form.Item>

                                    <Form.Item label="医疗机构层级" name="medTier">
                                        <Select placeholder="选择医疗机构层级" allowClear>
                                            {medTierList.map((item, index) => (
                                                <Select.Option value={item} key={index}>
                                                    {item}
                                                </Select.Option>
                                            ))}
                                        </Select>
                                    </Form.Item>

                                    <Form.Item label="等级">
                                        <Space>
                                            <Form.Item name="medLevel" className="medLevel-form-item">
                                                <Select placeholder="级别">
                                                    {medLevelList.map((item, index) => (
                                                        <Select.Option value={item} key={index}>
                                                            {item}
                                                        </Select.Option>
                                                    ))}
                                                </Select>
                                            </Form.Item>
                                            <Form.Item name="medGrade" className="medLevel-form-item">
                                                <Select placeholder="等次">
                                                    {medGradeList.map((item, index) => (
                                                        <Select.Option value={item} key={index}>
                                                            {item}
                                                        </Select.Option>
                                                    ))}
                                                </Select>
                                            </Form.Item>
                                        </Space>
                                    </Form.Item>

                                    <Form.Item label="国家医学中心" name="nationalMedicalCenter" valuePropName="checked" getValueFromEvent={value => (value ? 1 : 0)}>
                                        <Switch checkedChildren="是" unCheckedChildren="否" />
                                    </Form.Item>

                                    <Form.Item label="国家区域医疗中心">
                                        <Space>
                                            <Form.Item name="nationalRegionalMedicalCenter" valuePropName="checked" getValueFromEvent={value => (value ? 1 : 0)}>
                                                <Switch checkedChildren="是" unCheckedChildren="否" />
                                            </Form.Item>
                                            <Form.Item noStyle shouldUpdate>
                                                {({ getFieldValue }) => {
                                                    const isShow = getFieldValue('nationalRegionalMedicalCenter');
                                                    return (
                                                        !!isShow && (
                                                            <Form.Item
                                                                name="nationalRegionalMedicalCenterType"
                                                                rules={[
                                                                    {
                                                                        required: true,
                                                                        validator: async (_, value) => {
                                                                            if (value === 0 || !value) {
                                                                                throw new Error('请选择国家区域医疗中心类型（输入医院/输出医院）');
                                                                            }
                                                                        },
                                                                    },
                                                                ]}
                                                            >
                                                                <Radio.Group>
                                                                    <Radio value={1}>输入医院</Radio>
                                                                    <Radio value={2}>输出医院</Radio>
                                                                </Radio.Group>
                                                            </Form.Item>
                                                        )
                                                    );
                                                }}
                                            </Form.Item>
                                        </Space>
                                    </Form.Item>

                                    <Form.Item label="国家级临床重点专科" name="nationalKeyClinicalSpecialty">
                                        <Input.TextArea placeholder="输入国家级临床重点专科" rows={3} />
                                    </Form.Item>

                                    <Form.Item label="省级临床重点专科" name="provincialKeyClinicalSpecialty">
                                        <Input.TextArea placeholder="输入省级临床重点专科" rows={3} />
                                    </Form.Item>

                                    <Form.Item label="市级临床重点专科" name="municipalKeyClinicalSpecialty">
                                        <Input.TextArea placeholder="输入市级临床重点专科" rows={3} />
                                    </Form.Item>
                                </>
                            )}

                            <Form.Item
                                label="百度百科词条"
                                name="baiduBaikeUrl"
                                extra="填写百度百科词条链接，百度百科作为机构的扩展信息来源"
                                rules={[
                                    {
                                        type: 'url',
                                        message: '请输入正确的网址，如 https://baike.baidu.com/item/北京大学第三医院/2999448',
                                    },
                                ]}
                            >
                                <Input placeholder="输入百度百科词条链接，如 https://baike.baidu.com/item/北京大学第三医院/2999448" />
                            </Form.Item>

                            {/* 医疗机构 - 运营信息字段 */}
                            {orgType === 1 && (
                                <>
                                    <div className="module-title">运营信息</div>
                                </>
                            )}

                            {/* 医疗机构类型有以下字段：床位数、员工数、年门急诊量 */}
                            {orgType === 1 && (
                                <>
                                    <Form.Item label="床位数" name="bedCount" extra="填写具体床位数，概数请填整数">
                                        <InputNumber placeholder="输入床位数" in={0} />
                                    </Form.Item>

                                    <Form.Item label="员工数" name="employeeCount" extra="填写具体员工数，概数请填整数">
                                        <InputNumber placeholder="输入员工数" min={0} />
                                    </Form.Item>

                                    <Form.Item label="年门急诊量" name="annualOutpatientEmergVisits">
                                        <Space.Compact style={{ width: '100%' }}>
                                            <InputNumber placeholder="输入年门急诊量" min={0} style={{ width: 'calc(100% - 64px)' }} />
                                            <Input readOnly style={{ width: 64, textAlign: 'center' }} tabIndex={-1} value="万人次" />
                                        </Space.Compact>
                                    </Form.Item>

                                    <Form.Item label="院区" name="branchIds">
                                        <div onClick={addHospital} className="add-aliases">
                                            <i className="bi bi-plus-circle"></i>
                                            <span>添加院区</span>
                                        </div>

                                        {!id
                                            && branchList
                                            && !!branchList.length
                                            && branchList.map(item => (
                                                <div className={`hospital-list-item ${!!item.creditCode ? 'credit-code-item' : ''}`} key={item.id}>
                                                    {!!item.creditCode && (
                                                        <div className="credit-code" style={{ backgroundColor: getCreditCodeBackgroundColor(item.creditCode) }}>
                                                            登记号：{item.creditCode}
                                                        </div>
                                                    )}
                                                    <div className="hospital-list-item-title">
                                                        <div className="hospital-list-item-name">
                                                            <span>
                                                                {item.name}
                                                                <Text type="secondary"> #{item.id}</Text>
                                                            </span>
                                                            <span>{item.address}</span>
                                                        </div>
                                                    </div>
                                                    <Button type="link" onClick={() => removeHospital(item.id)}>
                                                        移除
                                                    </Button>
                                                </div>
                                            ))}

                                        {id
                                            && branchList
                                            && !!branchList.length
                                            && branchList.map(item => (
                                                <div className={`hospital-list-item ${!!item.creditCode ? 'credit-code-item' : ''}`} key={item.id}>
                                                    {!!item.creditCode && (
                                                        <div className="credit-code" style={{ backgroundColor: getCreditCodeBackgroundColor(item.creditCode) }}>
                                                            登记号：{item.creditCode}
                                                        </div>
                                                    )}
                                                    <div className="hospital-list-item-title">
                                                        <div className="hospital-list-item-name">
                                                            <span>
                                                                {item.name}
                                                                <Text type="secondary"> #{item.id}</Text>
                                                                {curMainBranchId === item.id ? (
                                                                    <>
                                                                        <Tag color="#f50" style={{ padding: '2px 4px', marginLeft: 8, lineHeight: '1.2em' }}>
                                                                            主院区
                                                                        </Tag>
                                                                        <Popconfirm title="确定要取消主院区？" onConfirm={() => setCurMainBranchId(null)} okText="确定" cancelText="取消">
                                                                            <Tag style={{ padding: '2px 4px', lineHeight: '1.2em' }}>取消</Tag>
                                                                        </Popconfirm>
                                                                    </>
                                                                ) : (
                                                                    <Tag onClick={() => setCurMainBranchId(item.id)} style={{ padding: '2px 4px', marginLeft: 8, lineHeight: '1.2em' }}>
                                                                        设为主院区
                                                                    </Tag>
                                                                )}
                                                            </span>
                                                            <span>{item.address}</span>
                                                        </div>
                                                    </div>
                                                    <div>
                                                        {item.id === id && <Text type="secondary">当前机构</Text>}
                                                        {item.id !== id && (
                                                            <>
                                                                <Space>
                                                                    <Link onClick={() => viewHospital(item.id)}>查看</Link>
                                                                    <Link onClick={() => editHospital(item.id)}>编辑</Link>
                                                                    <Link onClick={() => removeHospital(item.id)}>移除</Link>
                                                                </Space>
                                                            </>
                                                        )}
                                                    </div>
                                                </div>
                                            ))}
                                    </Form.Item>
                                </>
                            )}

                            {/* 企业有以下字段：营业执照、统一社会信用代码、法人代表、经营范围 */}
                            {orgType === 5 && (
                                <>
                                    <Form.Item name="bizLicense" label="营业执照" extra="图片尺寸不小于 300*300px，支持 JPG/PNG格式" className="logo-image">
                                        {bizLicenseList.length > 0 && <Image width={200} src={bizLicenseList[0]} className="share-image-area" alt="营业执照预览" />}
                                        <div>
                                            <Upload beforeUpload={beforeUpload} fileList={bizLicenseList} customRequest={e => uploadFile(e, 2)} maxCount={1}>
                                                <Button icon={<i className="bi bi-upload"></i>}>{bizLicenseList.length > 0 ? '更改图片' : '上传图片'}</Button>
                                            </Upload>
                                        </div>
                                    </Form.Item>

                                    <Form.Item label="统一社会信用代码" name="creditCode">
                                        <Input placeholder="输入统一社会信用代码" />
                                    </Form.Item>

                                    <Form.Item label="法人代表" name="legalRep">
                                        <Input placeholder="输入法人代表" />
                                    </Form.Item>

                                    <Form.Item label="经营范围" name="bizScope">
                                        <Input.TextArea placeholder="输入经营范围" rows={5} />
                                    </Form.Item>
                                </>
                            )}

                            <div className="module-title">联系信息</div>

                            <Form.Item label="所在地">
                                <div className="location-wrap">
                                    <Form.Item name="country" noStyle>
                                        <Select
                                            options={[
                                                { value: 'cn', label: '中国' },
                                                { value: 'oversea', label: '海外' },
                                            ]}
                                            placeholder="选择国家"
                                            allowClear
                                        />
                                    </Form.Item>
                                    <Form.Item noStyle shouldUpdate>
                                        {({ getFieldValue }) => {
                                            const areaType = getFieldValue('country') === 'cn';
                                            return (
                                                <>
                                                    {!!areaType && (
                                                        <Form.Item name="areas" noStyle>
                                                            <Cascader placeholder="选择所在地" options={regionOptions} onChange={handleAreasChange} showSearch />
                                                        </Form.Item>
                                                    )}
                                                </>
                                            );
                                        }}
                                    </Form.Item>
                                </div>
                            </Form.Item>

                            <Form.Item label="地址" name="address">
                                <Input placeholder="输入地址" maxLength={200} showCount />
                            </Form.Item>

                            <Form.Item label="邮编" name="postCode">
                                <Input placeholder="输入地址邮编" maxLength={20} showCount />
                            </Form.Item>

                            <Form.Item label="传真" name="fax">
                                <Input placeholder="输入传真" maxLength={20} showCount />
                            </Form.Item>

                            <Form.Item label="电子邮箱" name="email">
                                <Input placeholder="输入电子邮箱" maxLength={100} showCount />
                            </Form.Item>

                            <Form.Item label="联系电话" name="tel">
                                <Input placeholder="输入联系电话" maxLength={100} showCount />
                            </Form.Item>

                            <Form.Item label="网址" name="site" rules={[{ pattern: /^https?:\/\/(.+\.)?.+\..+/i, message: '请输入正确的网址，如 https://www.puh3.net.cn/' }]}>
                                <Input placeholder="输入机构官方网址，如 https://www.puh3.net.cn/" maxLength={255} />
                            </Form.Item>

                            <div className="module-title">其它信息</div>

                            <Form.Item name="files" label="附件" extra="支持图片（JPG/PNG）、Office 文档（Word/Excel/PPT）、PDF、PSD、Ai及压缩包，单个文件不超过 500MB" className="logo-image">
                                <div>
                                    <Upload beforeUpload={filesBeforeUpload} fileList={logoList} customRequest={e => uploadFile(e, 3)} maxCount={1}>
                                        <Button icon={<i className="bi bi-upload"></i>}>上传附件</Button>
                                    </Upload>
                                    {fileShowProgress && <Progress percent={fileProgressPercent} />}
                                </div>
                                {!!fileList.length
                                    && fileList.map((file, index) => (
                                        <div className="hospital-list-item" key={index}>
                                            <span>{file.name}</span>
                                            <span className="anticon" onClick={() => deleteFile(index)}>
                                                <i className="bi bi-x"></i>
                                            </span>
                                        </div>
                                    ))}
                            </Form.Item>

                            <Form.Item label="备注" name="remarks">
                                <Input.TextArea placeholder="输入备注" rows={4} />
                            </Form.Item>
                        </Form>
                    </Col>
                </Row>
            </Drawer>

            <Modal title="添加院区" open={addHospitalModal} onOk={handleOk} width={700} onCancel={handleCancel} className="add-hospital-modal" okButtonProps={{ disabled: !hasSelectedHospital }}>
                <Form form={addHospitalForm} layout="vertical" name="basic" autoComplete="off" onFinish={onSearchHospitalSubmit}>
                    <Alert title="通过所在地、和标识关键字检索医疗机构，添加院区；如未检索到医疗机构，可更换关键字重试，或新增机构。" type="warning" />

                    <div className="search-hospital-form">
                        <Form.Item name="areas">
                            <Cascader placeholder="选择所在地" options={regionOptions} changeOnSelect showSearch allowClear />
                        </Form.Item>
                        <Form.Item name="keyword">
                            <Input placeholder="ID/名称/别称/简称/显示名称" allowClear />
                        </Form.Item>
                        <Form.Item>
                            <Button htmlType="submit">检索</Button>
                        </Form.Item>
                    </div>

                    <div className="search-result-box" style={{ minHeight: 100 }}>
                        {searchHospitalList && (
                            <Form.Item label="检索到的医疗机构：" name="orgs" style={{ marginBottom: 0 }}>
                                {!!searchHospitalList.length
                                    && searchHospitalList.map((item, index) => (
                                        <div className="hospital-list-item" key={index}>
                                            <div className="hospital-list-item-title">
                                                {item.creditCode && <div className="hospital-list-item-no">登记号：{item.creditCode}</div>}
                                                <div className="hospital-list-item-name">
                                                    <span>
                                                        {item.name}
                                                        <Text type="secondary"> #{item.id}</Text>
                                                    </span>
                                                    <span>{item.address}</span>
                                                </div>
                                            </div>
                                            <Form.Item name={['orgs', index]} valuePropName="checked" className="hospital-list-item-checkbox" initialValue={isHospitalSelected(item.id)}>
                                                <Checkbox disabled={disabledHospital(item.id)} onChange={handleCheckboxChange}></Checkbox>
                                            </Form.Item>
                                        </div>
                                    ))}

                                {!searchHospitalList.length && <Text type="secondary">未检索到相关医疗机构</Text>}

                                {searchHospitalTotal > 0 && (
                                    <div style={{ marginTop: 16, textAlign: 'right' }}>
                                        <Pagination size="small" current={searchHospitalPage} pageSize={searchHospitalPageSize} total={searchHospitalTotal} showSizeChanger={false} onChange={handleSearchHospitalPageChange} />
                                    </div>
                                )}
                            </Form.Item>
                        )}
                    </div>
                </Form>
            </Modal>

            <Modal title="确认更改机构类型" open={orgTypeChangeModal} onOk={confirmOrgTypeChange} onCancel={cancelOrgTypeChange} okText="确认" cancelText="取消">
                <p>更改机构类型可能因字段数量不同而导致数据丢失。确认更改？</p>
            </Modal>

            {id ? <OperationLogDrawer open={openRecordDrawer} onClose={() => setOpenRecordDrawer(false)} entityType="organization" entityId={id} module="organization" maskClosable={false} /> : null}
        </>
    );
};

export default OrganizationAdd;
